import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { useSucursalesActivas } from "../lib/useSucursales";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Mail, Lock, ShieldCheck, Upload, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

function LogoBadge() {
  return (
    <div className="mx-auto w-32 rounded-[26px] bg-[#0A1F44] border border-[#1c3a6e] px-4 py-5 text-center shadow-2xl"
         style={{ boxShadow: "0 0 40px rgba(245,166,35,.15), 0 20px 40px rgba(0,0,0,.4)" }}>
      <svg viewBox="0 0 64 40" className="h-9 w-full mb-1" aria-hidden="true">
        <defs>
          <linearGradient id="lbWing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FDE68A" /><stop offset="50%" stopColor="#F5A623" /><stop offset="100%" stopColor="#B45309" />
          </linearGradient>
        </defs>
        <g fill="url(#lbWing)">
          <path d="M42 6c6 1 11 4 12 10-4-3-8-4-12-3 3-4 2-5 0-7z" />
          <path d="M6 26c14-1 26-5 40-18-3 9-9 14-17 18 8-1 14-3 21-8-4 8-12 14-24 15-8 .6-15 .2-20-.3 0-2 0-4 0-6.4z" />
          <path d="M10 31c10 0 19-2 29-8-5 6-11 8-18 10 5 0 9-1 14-4-4 6-11 8-19 8-4 .3-8 .1-11-.1 1-2 3-4 5-5.9z" opacity=".9" />
        </g>
      </svg>
      <div className="text-lg font-extrabold font-display brand-gold-text leading-none">Pya'elino</div>
      <div className="text-[9px] font-bold tracking-[0.35em] text-slate-200 mt-1">COURIER</div>
      <div className="text-sm font-bold italic brand-gold-text mt-1" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Entregado!</div>
    </div>
  );
}

const compressImage = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const max = 1000;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = img.width * scale; c.height = img.height * scale;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.6));
    };
    img.onerror = () => resolve(ev.target.result);
    img.src = ev.target.result;
  };
  reader.onerror = () => resolve(null);
  reader.readAsDataURL(file);
});

export default function Login() {
  const { login, register } = useAuth();
  const sucs = useSucursalesActivas();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [form, setForm] = useState({ name: "", email: "", password: "", whatsapp: "", token: "", sucursal: "ASU" });
  const [docFrente, setDocFrente] = useState(null);
  const [docDorso, setDocDorso] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [pending, setPending] = useState(false);
  const [aceptaContrato, setAceptaContrato] = useState(false);
  const [showContrato, setShowContrato] = useState(false);
  const [contrato, setContrato] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openContrato = async () => {
    if (!contrato) { try { const { data } = await api.get("/contrato"); setContrato(data); } catch { /* ignore */ } }
    setShowContrato(true);
  };

  const onDoc = (which) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = await compressImage(file);
    which === "frente" ? setDocFrente(data) : setDocDorso(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "register" && (!docFrente || !docDorso)) {
      setErr("Adjuntá la foto del documento (frente y dorso) para continuar.");
      return;
    }
    if (mode === "register" && !aceptaContrato) {
      setErr("Debés aceptar el Contrato de Prestación de Servicios y Términos y Condiciones.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password); navigate("/app");
      } else if (mode === "register") {
        const res = await register({ name: form.name, email: form.email, password: form.password, whatsapp: form.whatsapp, sucursal: form.sucursal, doc_frente: docFrente, doc_dorso: docDorso, acepta_contrato: aceptaContrato });
        if (res.pending) setPending(true);
        else navigate("/app");
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setResetToken(data.reset_token || ""); toast.success("Token generado (flujo simulado)");
        setMode("reset"); setForm({ ...form, token: data.reset_token || "" });
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: form.token, password: form.password });
        toast.success("Contraseña actualizada. Iniciá sesión."); setMode("login");
      }
    } catch (e2) { setErr(apiErr(e2)); }
    finally { setLoading(false); }
  };

  const field = "w-full rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-3.5 py-3 text-[15px] text-slate-900 outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100 focus:bg-white transition";
  const titles = { login: "Bienvenido", register: "Crear cuenta", forgot: "Recuperar contraseña", reset: "Nueva contraseña" };

  // Pending approval screen
  if (pending) {
    return (
      <div className="min-h-screen bg-[#0A1F44] flex items-center justify-center p-6" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #16305e 0%, #0A1F44 45%, #081734 100%)" }}>
        <div className="w-full max-w-md text-center fade-up">
          <LogoBadge />
          <div className="mt-8 bg-white rounded-3xl shadow-2xl p-8">
            <div className="mx-auto h-16 w-16 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
              <Clock className="h-8 w-8 text-[#F5A623]" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold text-[#0A1F44] font-display">¡Registro exitoso!</h1>
            <p className="mt-2 text-sm text-[#64748B]">Tu cuenta quedó <b className="text-[#0A1F44]">pendiente de aprobación</b> por un administrador. Te avisaremos cuando puedas ingresar.</p>
            <button onClick={() => { setPending(false); setMode("login"); setForm({ name: "", email: "", password: "", whatsapp: "", token: "" }); setDocFrente(null); setDocDorso(null); }}
              data-testid="pending-back-btn" className="mt-6 w-full rounded-xl bg-[#0A1F44] hover:bg-[#132D5C] text-white font-semibold py-3 text-sm transition-colors">
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1F44] flex flex-col items-center justify-center p-5 sm:p-6"
         style={{ backgroundImage: "radial-gradient(circle at 20% 8%, #16305e 0%, #0A1F44 45%, #081734 100%)" }}>
      <div className="w-full max-w-md fade-up">
        <div className="mb-6"><LogoBadge /></div>

        <div className="bg-white rounded-3xl shadow-2xl p-7 sm:p-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#0A1F44] pl-2 pr-3.5 py-1.5">
            <span className="h-5 w-5 rounded-full bg-[#F5A623]/20 flex items-center justify-center"><ShieldCheck className="h-3.5 w-3.5 text-[#F5A623]" /></span>
            <span className="text-xs font-semibold text-white">Plataforma segura</span>
          </div>

          <h1 className="mt-4 text-4xl font-extrabold text-[#0A1F44] font-display">{titles[mode]}</h1>
          <p className="mt-1.5 text-[15px] text-[#64748B] leading-snug">
            {mode === "login" && "Ingresá al sistema operativo y portal de clientes de Pya'elino Courier."}
            {mode === "register" && "Creá tu cuenta. Necesitás adjuntar tu documento; un administrador la aprobará."}
            {mode === "forgot" && "Te generamos un token de recuperación (flujo simulado)."}
            {mode === "reset" && "Pegá el token y definí tu nueva contraseña."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div className="relative">
                <input className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-[15px] outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100 focus:bg-white" placeholder="Nombre completo" value={form.name} onChange={set("name")} required data-testid="reg-name-input" />
              </div>
            )}
            {(mode === "login" || mode === "register" || mode === "forgot") && (
              <div>
                <label className="block text-sm font-semibold text-[#0A1F44] mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 h-5 w-5 text-slate-400" />
                  <input className={field} type="email" placeholder="tucorreo@email.com" value={form.email} onChange={set("email")} required data-testid="login-email-input" />
                </div>
              </div>
            )}
            {mode === "register" && (
              <div>
                <label className="block text-sm font-semibold text-[#0A1F44] mb-1.5">Sucursal / ciudad de origen (desde donde enviás)</label>
                <select className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-[15px] outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100 focus:bg-white" value={form.sucursal} onChange={set("sucursal")} data-testid="reg-sucursal-select">
                  {sucs.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
                </select>
              </div>
            )}
            {mode === "register" && (
              <div className="relative">
                <input className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-[15px] outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100 focus:bg-white" placeholder="WhatsApp (ej. 0981123456)" value={form.whatsapp} onChange={set("whatsapp")} data-testid="reg-whatsapp-input" />
              </div>
            )}
            {mode === "reset" && (
              <input className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-[15px] outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100 focus:bg-white" placeholder="Token de recuperación" value={form.token} onChange={set("token")} required data-testid="reset-token-input" />
            )}
            {(mode === "login" || mode === "register" || mode === "reset") && (
              <div>
                <label className="block text-sm font-semibold text-[#0A1F44] mb-1.5">Contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-3.5 h-5 w-5 text-slate-400" />
                  <input className={field} type="password" placeholder={mode === "reset" ? "Nueva contraseña" : "Tu contraseña"} value={form.password} onChange={set("password")} required data-testid="login-password-input" />
                </div>
              </div>
            )}

            {mode === "register" && (
              <div className="grid grid-cols-2 gap-3">
                {[["frente", docFrente, setDocFrente], ["dorso", docDorso, setDocDorso]].map(([which, val]) => (
                  <label key={which} className="cursor-pointer" data-testid={`doc-${which}-label`}>
                    <span className="block text-xs font-semibold text-[#0A1F44] mb-1.5 capitalize">Documento ({which})</span>
                    <div className={`rounded-xl border-2 border-dashed ${val ? "border-emerald-300 bg-emerald-50" : "border-slate-300 bg-slate-50"} h-24 flex items-center justify-center overflow-hidden`}>
                      {val ? <img src={val} alt={which} className="h-full w-full object-cover" /> : <span className="flex flex-col items-center text-slate-400 text-[11px] font-medium"><Upload className="h-5 w-5 mb-1" /> Subir foto</span>}
                    </div>
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onDoc(which)} data-testid={`doc-${which}-input`} />
                  </label>
                ))}
              </div>
            )}

            {resetToken && mode === "reset" && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2.5">
                Token generado: <span className="font-mono font-bold break-all">{resetToken}</span>
              </div>
            )}
            {mode === "register" && (
              <label className="flex items-start gap-2 text-xs text-[#0A1F44] bg-slate-50 border border-slate-200 rounded-lg p-3 cursor-pointer">
                <input type="checkbox" checked={aceptaContrato} onChange={(e) => setAceptaContrato(e.target.checked)} data-testid="reg-acepta-contrato-check" className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#0A1F44]" />
                <span>He leído y acepto el <button type="button" onClick={openContrato} className="font-bold underline" data-testid="reg-ver-contrato-btn">Contrato de Prestación de Servicios y Términos y Condiciones</button>.</span>
              </label>
            )}
            {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="auth-error">{err}</div>}

            {mode === "login" && (
              <div className="text-right -mt-1">
                <button type="button" className="text-sm font-bold text-[#0A1F44] hover:underline" onClick={() => { setMode("forgot"); setErr(""); }} data-testid="goto-forgot">¿Olvidaste tu contraseña?</button>
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full rounded-xl bg-[#0A1F44] hover:bg-[#132D5C] text-white font-bold py-3.5 text-[15px] transition-colors disabled:opacity-60">
              {loading ? "Procesando..." : mode === "login" ? "Iniciar sesión" : mode === "register" ? "Crear cuenta" : titles[mode]}
            </button>
          </form>

          {mode === "login" && (
            <>
              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <button type="button" onClick={() => toast.info("El acceso con Google estará disponible próximamente.")} data-testid="google-btn"
                className="w-full flex items-center justify-center gap-3 rounded-xl border border-slate-300 py-3.5 text-[15px] font-semibold text-[#0A1F44] hover:bg-slate-50 transition-colors">
                <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.4 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.06L5.84 8.9C6.71 6.3 9.14 4.75 12 4.75z"/></svg>
                Continuar con Google
              </button>
            </>
          )}

          <div className="mt-5 text-center text-sm">
            {mode === "login"
              ? <span className="text-[#64748B]">¿No tenés cuenta? <button className="text-[#0A1F44] font-bold hover:underline" onClick={() => { setMode("register"); setErr(""); }} data-testid="goto-register">Crear cuenta</button></span>
              : <button className="text-[#0A1F44] font-bold hover:underline" onClick={() => { setMode("login"); setErr(""); }} data-testid="goto-login">Volver a iniciar sesión</button>}
          </div>

          {mode === "login" && (
            <>
              <div className="mt-5 pt-4 border-t border-slate-100 text-center">
                <p className="text-sm font-bold text-[#0A1F44]">Vos vendés. Nosotros entregamos y cobramos por vos.</p>
              </div>
            </>
          )}
        </div>

        <p className="mt-5 text-center text-xs text-slate-400">
          ¿Sos destinatario? Consultá tu envío en <a href="/track" className="text-[#F5A623] font-semibold">Tracking público</a>
        </p>
      </div>

      <Dialog open={showContrato} onOpenChange={setShowContrato}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] flex flex-col" data-testid="reg-contrato-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Contrato de servicios · v{contrato?.version || "…"}</DialogTitle>
            <DialogDescription>Última actualización: {contrato?.fecha || "…"}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto rounded-lg border border-slate-200 p-4 space-y-4 text-sm" data-testid="reg-contrato-texto">
            {!contrato && <p className="text-[#64748B]">Cargando contrato…</p>}
            {contrato && (
              <>
                {contrato.intro.map((p, i) => <p key={i} className="text-[#64748B] leading-relaxed">{p}</p>)}
                {contrato.clausulas.map((cl) => (
                  <div key={cl.n}>
                    <h4 className="font-semibold text-[#0A1F44]">{cl.n}. {cl.titulo}</h4>
                    {cl.parrafos.map((p, i) => <p key={i} className="text-[#64748B] mt-1 leading-relaxed">{p}</p>)}
                  </div>
                ))}
              </>
            )}
          </div>
          <button onClick={() => { setAceptaContrato(true); setShowContrato(false); }} data-testid="reg-contrato-aceptar-btn"
            className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-2.5 text-sm">
            Aceptar contrato
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
